import React, { useState } from "react";
import { Dropdown, DropdownButton, Form, Spinner } from "react-bootstrap";
import { ArrowClockwise, Search } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { getDayjs, weekOf } from "../../../../helpers/dateHelpers";
import { sortedFSGroups } from "../../../../helpers/fsgroup";
import {
  getHospitalityCleaningConflictLabel,
  getOverlappingAssignmentLabel,
} from "../../../../helpers/conflictDetection";
import { containsFuzzy } from "../../../../helpers/locale";
import { userCompare } from "../../../../helpers/user";
import { ISODateString } from "../../../../types/date";
import { Absence } from "../../../../types/scheduling/absence";
import { AssignmentNotification, UserPrivilegeMap } from "../../../../types/scheduling/meetings";
import { OtherSchedule } from "../../../../types/scheduling/other";
import { CongSettings } from "../../../../types/scheduling/settings";
import { TalkMod, WMCorePartTitleAsset, WMCoreParts, WMSchedule } from "../../../../types/scheduling/weekend";
import User from "../../../../types/user";
import { DropdownFilter, OverlappingAssignment } from "../../common";
import { NotificationIcon, NotificationOptions } from "../../notificationStatus";
import { AssignmentSummary, SearchAssigneeModal } from "../../RecentAssignments";
import { UserAssignmentDropdowns } from "../../UserAssignmentDropdowns";
import { useAssignmentNotifications, useOverlappingWMAssignments, usePartAssignment } from "../hooks";
import { canUpdateWeekendSchedules } from "../wm_common";

interface PartAssignmentDropdownProps {
  part: WMCoreParts;
  schedule: WMSchedule;
  langGroupId: number;
  userPrivilegeMap: UserPrivilegeMap;
  userMap: Map<number, User>;
  refresh: () => void;
  notificationDates: { start: ISODateString; end: ISODateString };
  notifications?: AssignmentNotification[];
  _scheduleDates?: { from: ISODateString; to: ISODateString };
  assignmentMap: Map<number, AssignmentSummary[]>;
  meetingDate: ISODateString;
  absences?: Absence[];
  congSettings: CongSettings;
  noAutoFill?: boolean;
  disabled?: boolean;
  fsGroups?: any[];
  cleaningSchedules?: OtherSchedule[];
}

export const PartAssignmentDropdown: React.FC<PartAssignmentDropdownProps> = ({
  part,
  schedule,
  langGroupId,
  userPrivilegeMap,
  userMap,
  refresh,
  notificationDates,
  notifications,
  assignmentMap,
  meetingDate,
  absences,
  congSettings,
  noAutoFill,
  disabled,
  fsGroups,
  cleaningSchedules,
}) => {
  const { t } = useTranslation();
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [needSetFocus, setNeedSetFocus] = useState(false);

  // Helper function to get the assigned user ID for this part
  function getAssigneeId(): number {
    switch (part) {
      case WMCoreParts.Chairman:
        return schedule.wm_chairman;
      case WMCoreParts.Reader:
        return schedule.wm_reader;
      case WMCoreParts.Host:
        return schedule.host;
      case WMCoreParts.WTConductor:
        return schedule.wt_conductor ?? congSettings.wt_conductor ?? 0;
      case WMCoreParts.OpeningPrayer:
        return schedule.openprayer ?? 0;
      case WMCoreParts.ClosingPrayer:
        return schedule.closeprayer ?? 0;
      case WMCoreParts.Interpreter:
        return schedule.interpreter ?? 0;
    }
  }

  // Get the assigned user ID for this part
  const assignedUserId = getAssigneeId();

  // Get the speaker and outgoing speaker IDs for conflict checking
  const speakerId = schedule.speaker?.userId;
  const outgoingSpeakerIds = (schedule.out?.map((o) => o.speaker?.userId).filter(Boolean) as number[]) || [];

  // Get the WT conductor ID for conflict checking
  const wtConductorId = schedule.wt_conductor ?? congSettings.wt_conductor ?? 0;
  const isNotRegularWTConductor = schedule.wt_conductor && schedule.wt_conductor !== congSettings.wt_conductor;

  // Check if this is a CO talk or event
  const coTalkMod = schedule.talk_mod === TalkMod.CO || schedule.event?.event === "co";

  // Check if there's a speaker assigned
  const hasSpeaker = !!schedule.talk_mod || !!schedule.speaker?.id;

  // Check if the speaker should be assigned to closing prayer
  const isSpeakerClosingPrayer =
    part === WMCoreParts.ClosingPrayer && congSettings.wm_speaker_closing_prayer && !assignedUserId && hasSpeaker;

  // Get the part name for privilege checking
  const wmPart = getWMPart();

  // Get the list of users who can be assigned to this part
  const userIds = userPrivilegeMap[wmPart] || [];
  const possibleUsers: User[] = userIds
    .filter((userId) => userId !== assignedUserId)
    .flatMap((userId) => userMap.get(userId) || [])
    .sort(userCompare);

  // Filter users based on search term
  const filteredUsers = possibleUsers.filter((u) =>
    assigneeFilter ? containsFuzzy(u.displayName, assigneeFilter) : true,
  );

  // Get group info for hospitality assignments
  const groupInfo = fsGroups ? sortedFSGroups(fsGroups, userMap) : [];

  // Get the dropdown display value
  const ddmValue = getDDMValue();

  // Check if the clear button should be shown
  const showClear = !(part === WMCoreParts.WTConductor && assignedUserId === congSettings.wt_conductor);

  // Check if this is a past week
  const isPastWeek = getDayjs(schedule.date).isBefore(weekOf(new Date()), "day");

  // Use custom hooks for assignment, notifications, and overlapping assignments
  const { saving, assignPart, autoAssignPart } = usePartAssignment({
    part,
    schedule,
    langGroupId,
    onRefresh: refresh,
  });

  const { notification, notificationState, hasConflictingAbsence } = useAssignmentNotifications({
    part,
    scheduleId: schedule.id,
    assigneeId: assignedUserId,
    meetingDate,
    notifications,
    absences,
  });

  const overlappingAssignmentLabel = useOverlappingWMAssignments({
    assigneeId: assignedUserId,
    partType: part,
    wtConductorId,
    speakerId,
    outgoingSpeakerIds,
  });

  // catches conflicts useOverlappingWMAssignments doesn't know about (e.g. a midweek part or an
  // AV/attendant assignment the same week), since it only checks speaker/WTC relationships and
  // doesn't consult the assignmentMap. Respect this week's WT conductor override (schedule.wt_conductor)
  // rather than only the congregation-wide default, matching wtConductorId above.
  const overlappingAssignmentMapLabel = assignedUserId
    ? getOverlappingAssignmentLabel({
        userId: assignedUserId,
        assignDate: meetingDate,
        partType: part,
        assignmentMap,
        congSettings: { ...congSettings, wt_conductor: wtConductorId },
      })
    : null;

  // when hospitality is assigned by group, flag it as a potential conflict if that same group
  // is also assigned to clean the hall this week
  const cleaningConflictLabel =
    part === WMCoreParts.Host && congSettings.hospitality_by_group
      ? getHospitalityCleaningConflictLabel({
          hostUserId: assignedUserId,
          weekendDate: schedule.date,
          cleaningSchedules: cleaningSchedules ?? [],
          congSettings,
        })
      : null;

  const conflictLabel =
    overlappingAssignmentLabel ?? overlappingAssignmentMapLabel ?? cleaningConflictLabel ?? undefined;

  // Helper function to get the display value for the dropdown
  function getDDMValue(): string {
    const assigneeName = userMap.get(assignedUserId)?.displayName ?? "";
    switch (part) {
      case WMCoreParts.ClosingPrayer: {
        const speakerName = coTalkMod ? talkModSpeaker(schedule, t) : nameOfUser(schedule.speaker).trim();
        const speakerForClosingPrayer = isSpeakerClosingPrayer ? speakerName : "";
        return assigneeName || speakerForClosingPrayer;
      }
      case WMCoreParts.Host: {
        const groupDdmValue = congSettings.hospitality_by_group
          ? groupInfo.find((g) => g.overseerId === assignedUserId)?.displayName
          : undefined;
        return groupDdmValue ?? assigneeName;
      }
      default:
        return assigneeName;
    }
  }

  // Helper function to get the part name for privilege checking
  function getWMPart() {
    switch (part) {
      case WMCoreParts.OpeningPrayer:
        return "openprayer";
      case WMCoreParts.ClosingPrayer:
        return "closeprayer";
      default:
        return part;
    }
  }

  // Helper function to get the speaker name for talk mods
  function talkModSpeaker(schedule: WMSchedule, t: any): string {
    if (schedule.talk_mod === "tbd") return t("schedules.weekend.tbd");
    if (schedule.talk_mod === "stream") return t("schedules.stream");
    if (schedule.speaker.firstname) return schedule.speaker.firstname;
    if (schedule.event?.event === "co") return t("general.circuit-overseer");
    return t("schedules.weekend.tbd");
  }

  // Helper function to get the name of a user
  function nameOfUser(user: any): string {
    if (!user) return "";
    return `${user.firstname || ""} ${user.lastname || ""}`.trim();
  }

  return (
    <>
      <SearchAssigneeModal
        date={meetingDate}
        assignee={userMap.get(assignedUserId)}
        partType={part}
        possibleAssignees={possibleUsers}
        show={showAssignmentModal}
        setShow={setShowAssignmentModal}
        assign={assignPart}
        assignmentMap={assignmentMap}
        userMap={userMap}
        langGroupId={langGroupId}
        heading={
          <div>
            <div className="mx-auto">
              <h5>{t(WMCorePartTitleAsset[part])}</h5>
            </div>
          </div>
        }
      />

      <Form.Group className="d-flex flex-column">
        <Form.Label className={"fw-bolder text-muted mb-1" + (disabled ? "text-muted" : "")}>
          {t(WMCorePartTitleAsset[part])}
        </Form.Label>
        <DropdownButton
          onToggle={(nextShow: boolean) => {
            if (!nextShow) setAssigneeFilter("");
            setNeedSetFocus(nextShow);
          }}
          title={
            <>
              {saving ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <span>
                  {!!ddmValue && !isPastWeek && !isSpeakerClosingPrayer && (
                    <>
                      {(isNotRegularWTConductor || notificationState || hasConflictingAbsence) && (
                        <NotificationIcon nstate={notificationState} hasAbsence={hasConflictingAbsence} />
                      )}
                      {!!conflictLabel && <OverlappingAssignment className="ms-1" label={conflictLabel} />}
                    </>
                  )}
                </span>
              )}
              <span className="ms-1 me-1">{ddmValue || <i>{t("general.none-selected")}</i>}</span>
            </>
          }
          variant="secondary"
          className="dropdown-bounded"
          disabled={!canUpdateWeekendSchedules() || saving || disabled}
        >
          <NotificationOptions
            startDate={notificationDates.start}
            endDate={notificationDates.end}
            nstate={notificationState}
            notification={notification}
          />
          {!ddmValue && !noAutoFill && (
            <Dropdown.Item onClick={autoAssignPart}>
              <ArrowClockwise />️ <i>{t("schedules.fill.auto")}</i>
            </Dropdown.Item>
          )}
          {!!ddmValue && showClear && (
            <Dropdown.Item onClick={() => assignPart(0)}>
              ❌ <i>{t("schedules.fill.clear")}</i>
            </Dropdown.Item>
          )}
          <Dropdown.Item onClick={() => setShowAssignmentModal(true)}>
            <Search /> <i>{t("schedules.assignment.list-title")}</i>
          </Dropdown.Item>
          <Dropdown.Divider />
          <DropdownFilter key={0} filter={assigneeFilter} setFilter={setAssigneeFilter} setFocus={needSetFocus} />
          {part === WMCoreParts.Host && congSettings.hospitality_by_group ? (
            groupInfo.map((fsg) => (
              <Dropdown.Item key={fsg.overseerId} onClick={() => assignPart(fsg.overseerId)}>
                {fsg.displayName}
              </Dropdown.Item>
            ))
          ) : (
            <UserAssignmentDropdowns
              users={filteredUsers}
              assignDate={meetingDate}
              langGroupId={langGroupId}
              assign={assignPart}
              assigneeFilter={assigneeFilter}
              privilegeAsset={WMCorePartTitleAsset[part]}
              assignmentMap={assignmentMap}
              partType={part}
            />
          )}
        </DropdownButton>
      </Form.Group>
    </>
  );
};
